import React, { useState, useEffect } from 'react';
import { PanelHeader, Button, TextArea, TextField } from '@keplerco/core';
import { useAppActions } from '../../../../overmind';
import { AgentDto } from '../../../../overmind/actions/ai/ai-agents';
import { AgentSchema, executeTypedAgentRequest } from '../../../../overmind/actions/ai/ai-generic-agent';
import { FocusPanelLoaderLayer } from '../../../../components/general/loading-state/loader-layers/focus-panel-loader-layer/focus-panel-loader-layer';
import { PagePath } from '../../../../navigation/navigation.enums';
import { FetchType } from '../../../../enums';
import { AIAgentType } from '../../../../enums/ai-agent-type';
import { EmptyState } from '../../../../components/general/empty-state/empty-state';
import { createEmptyObjectFromSchema, handleArrayItemChange, handleFieldChange, handleNestedFieldChange, handlePrimitiveArrayItemChange, prepareRequest } from './test-agent.helpers';
import styles from './test-agent.module.css';
import classNames from 'classnames';

export function TestAgentPanel({ agent, companySlug, onExecute }: { agent: AgentDto; companySlug: string; onExecute: (response: any) => void }): JSX.Element {
  const actions = useAppActions();

  const [agentSchema, setAgentSchema] = useState<AgentSchema>();
  const [formData, setFormData] = useState<Record<string, any>>({});

  useEffect(() => {
    async function getData() {
      actions.startLoader({ path: PagePath.aiManagementAgent, type: FetchType.DialogFetching });

      const agentSchemaResponse = await actions.getAgentSchema(agent.agentTypeName);
      setAgentSchema(agentSchemaResponse);

      const initialData: Record<string, any> = {};

      // TODO: error handling
      if (!!agentSchemaResponse?.requestSchema?.properties) {
        Object.keys(agentSchemaResponse.requestSchema.properties).forEach(key => {
          const property = agentSchemaResponse.requestSchema.properties[key];

          if (property.type === 'array' && property.items) {
            // Initialize arrays with one empty item if items is defined
            if (property.items.properties) {
              initialData[key] = [createEmptyObjectFromSchema(property.items)];
            } else {
              initialData[key] = [];
            }
          } else if (property.type === 'object' && property.properties) {
            // Initialize objects with empty values
            initialData[key] = createEmptyObjectFromSchema(property);
          } else if (property.type === 'integer' || property.type === 'number') {
            // Initialize numeric values
            initialData[key] = property.default !== undefined ? property.default : 0;
          } else {
            // Initialize string values
            initialData[key] = property.default !== undefined ? property.default : '';
          }
        });
      }

      setFormData(initialData);

      actions.stopLoader(PagePath.aiManagementAgent);
    }

    getData();
  }, [agent]);

  function handleAddArrayItem(key: string, itemSchema: any) {
    setFormData(currentState => {
      // If it's an array of objects
      if (itemSchema.properties) {
        const newItem = createEmptyObjectFromSchema(itemSchema);
        return {
          ...currentState,
          [key]: [...(currentState[key] || []), newItem],
        };
      }
      // If it's an array of primitive types
      else if (itemSchema.type) {
        let defaultValue: string | number | boolean = '';

        // Set appropriate default value based on type
        if (itemSchema.type === 'string') {
          defaultValue = '';
        } else if (itemSchema.type === 'integer' || itemSchema.type === 'number') {
          defaultValue = 0;
        } else if (itemSchema.type === 'boolean') {
          defaultValue = false;
        }

        return {
          ...currentState,
          [key]: [...(currentState[key] || []), defaultValue],
        };
      }
      // Default case
      return {
        ...currentState,
        [key]: [...(currentState[key] || []), {}],
      };
    });
  }

  function handleRemoveArrayItem(arrayField: string, index: number) {
    setFormData(prev => {
      const newArray = [...prev[arrayField]];
      newArray.splice(index, 1);
      return {
        ...prev,
        [arrayField]: newArray,
      };
    });
  }

  function renderObjectField(key: string, label: string, itemSchema: any, value: any) {
    if (!itemSchema.properties) {
      return null;
    }

    return (
      <div key={key} className={classNames('card', styles.container)}>
        <h3>{label}</h3>

        <div className={styles.container}>
          {Object.keys(itemSchema.properties).map(propKey => {
            const propSchema = itemSchema.properties[propKey];
            const propLabel = propSchema.title || propKey;

            if (propSchema.type === 'string' && propSchema.format === 'textarea') {
              return <TextArea key={propKey} label={propLabel} value={value[propKey] || ''} onChange={e => setFormData(currentState => handleNestedFieldChange(currentState, key, propKey, e.target.value, propSchema.type))} responsive rows={3} />;
            }

            if (propSchema.type === 'integer' || propSchema.type === 'number') {
              return <TextField key={propKey} type="number" label={propLabel} value={value[propKey] || ''} onChange={e => setFormData(currentState => handleNestedFieldChange(currentState, key, propKey, e.target.value, propSchema.type))} responsive />;
            }

            return <TextField key={propKey} type="text" label={propLabel} value={value[propKey] || ''} onChange={e => setFormData(currentState => handleNestedFieldChange(currentState, key, propKey, e.target.value, propSchema.type))} responsive />;
          })}
        </div>
      </div>
    );
  }

  function renderArrayField(key: string, label: string, itemSchema: any, items: any[]) {
    return (
      <div key={key} className={classNames('card', styles.container)}>
        <div className={styles.header}>
          <h3>{label}</h3>
          <Button type="button" onClick={() => handleAddArrayItem(key, itemSchema)}>
            Add Item
          </Button>
        </div>

        {items.length === 0 ? (
          <EmptyState title="No items added yet" subtitle="Click 'Add Item' to add one" />
        ) : (
          <div className={styles.container}>
            {items.map((item, index) => (
              <div key={index} className={classNames('card', styles.container)}>
                <div className={styles.header}>
                  <h4>Item {index + 1}</h4>
                  <Button type="button" onClick={() => handleRemoveArrayItem(key, index)}>
                    Remove
                  </Button>
                </div>

                <div className={styles.container}>
                  {itemSchema.properties &&
                    Object.keys(itemSchema.properties).map(propKey => {
                      const propSchema = itemSchema.properties[propKey];
                      const propLabel = propSchema.title || propKey;

                      if (propSchema.type === 'integer' || propSchema.type === 'number') {
                        return <TextField key={propKey} type="number" label={propLabel} value={item[propKey] || ''} onChange={e => setFormData(currentState => handleArrayItemChange(currentState, key, index, propKey, e.target.value, propSchema.type))} responsive />;
                      }

                      return <TextField key={propKey} type="text" label={propLabel} value={item[propKey] || ''} onChange={e => setFormData(currentState => handleArrayItemChange(currentState, key, index, propKey, e.target.value, propSchema.type))} responsive />;
                    })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  function renderStringArrayField(key: string, label: string, itemSchema: any, items: string[]) {
    return (
      <div key={key} className={classNames('card', styles.container)}>
        <div className={styles.header}>
          <h3>{label}</h3>
          <Button type="button" onClick={() => handleAddArrayItem(key, itemSchema)}>
            Add Item
          </Button>
        </div>

        {items.length === 0 ? (
          <EmptyState title="No items added yet" subtitle="Click 'Add Item' to add one" />
        ) : (
          <div className={styles.container}>
            {items.map((item, index) => (
              <div key={index} className={classNames('card', styles.item)}>
                <TextField type="text" label={`Item ${index + 1}`} value={item || ''} onChange={e => setFormData(currentState => handlePrimitiveArrayItemChange(currentState, key, index, e.target.value, itemSchema.type))} responsive />

                <Button type="button" onClick={() => handleRemoveArrayItem(key, index)}>
                  Remove
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  function renderPrimitiveArrayField(key: string, label: string, itemSchema: any, items: any[]) {
    return (
      <div key={key} className={classNames('card', styles.container)}>
        <div className={styles.header}>
          <h3>{label}</h3>
          <Button type="button" onClick={() => handleAddArrayItem(key, itemSchema)}>
            Add Item
          </Button>
        </div>

        {items.length === 0 ? (
          <EmptyState title="No items added yet" subtitle="Click 'Add Item' to add one" />
        ) : (
          <div className={styles.container}>
            {items.map((item, index) => (
              <div key={index} className={classNames('card', styles.item)}>
                {itemSchema.type === 'boolean' ? (
                  <label>
                    <input type="checkbox" checked={!!item} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(currentState => handlePrimitiveArrayItemChange(currentState, key, index, e.target.checked, itemSchema.type))} />
                  </label>
                ) : itemSchema.type === 'integer' || itemSchema.type === 'number' ? (
                  <TextField type="number" label={`Item ${index + 1}`} value={item !== undefined ? item.toString() : ''} onChange={e => setFormData(currentState => handlePrimitiveArrayItemChange(currentState, key, index, e.target.value, itemSchema.type))} responsive />
                ) : (
                  <TextField type="text" label={`Item ${index + 1}`} value={item || ''} onChange={e => setFormData(currentState => handlePrimitiveArrayItemChange(currentState, key, index, e.target.value, itemSchema.type))} responsive />
                )}

                <Button type="button" onClick={() => handleRemoveArrayItem(key, index)}>
                  Remove
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  function renderFormFields() {
    return Object.keys(agentSchema?.requestSchema.properties).map(key => {
      const property = agentSchema?.requestSchema.properties[key];
      const label = property.title || key;

      // Get custom label if available
      const customLabel = agent.propertyLabelOverrides?.find(override => override.propertyName === key)?.label;

      const fieldLabel = customLabel || label;

      // Handle array of objects
      if (property.type === 'array' && property.items) {
        if (property.items.type === 'object') {
          return renderArrayField(key, fieldLabel, property.items, formData[key] || []);
        } else if (property.items.type === 'string') {
          return renderStringArrayField(key, fieldLabel, property.items, formData[key] || []);
        } else {
          return renderPrimitiveArrayField(key, fieldLabel, property.items, formData[key] || []);
        }
      }

      // Handle object
      if (property.type === 'object' && property.properties) {
        return renderObjectField(key, fieldLabel, property, formData[key] || {});
      }

      // Handle textarea
      if (property.type === 'string' && property.format === 'textarea') {
        return <TextArea key={key} label={fieldLabel} value={formData[key]} onChange={event => setFormData(currentState => handleFieldChange(currentState, key, event.target.value, property.type))} responsive />;
      }

      // Handle number
      if (property.type === 'integer' || property.type === 'number') {
        return <TextField key={key} type="number" label={fieldLabel} value={formData[key]} onChange={event => setFormData(currentState => handleFieldChange(currentState, key, event.target.value, property.type))} responsive />;
      }

      // Handle default text field
      return <TextField key={key} type="text" label={fieldLabel} value={formData[key]} onChange={event => setFormData(currentState => handleFieldChange(currentState, key, event.target.value, property.type))} responsive />;
    });
  }

  return (
    <FocusPanelLoaderLayer path={PagePath.aiManagementAgent}>
      <div className="panelContent">
        <PanelHeader title={`Test ${agent?.displayName || 'Agent'}`} subtitle={agent.description} divider />

        <div className={styles.container}>{!!agentSchema?.requestSchema.properties ? renderFormFields() : <EmptyState title="No input parameters available for this agent" />}</div>

        <footer className="panelFooter" style={{ justifyContent: 'flex-end' }}>
          <Button
            type="button"
            filled
            onClick={async () => {
              actions.startLoader({ path: PagePath.aiManagementAgent, type: FetchType.Sending });

              const requestData = prepareRequest(formData, agentSchema?.requestSchema.properties);
              const result = await executeTypedAgentRequest(agent.agentTypeName as AIAgentType, requestData, companySlug);
              // TODO: error handling
              onExecute(result);

              actions.stopLoader(PagePath.aiManagementAgent);
            }}
          >
            Execute
          </Button>
        </footer>
      </div>
    </FocusPanelLoaderLayer>
  );
}
